import React, { useState, useEffect } from 'react';
import {
  Rocket, Zap, CheckCircle2, FileText, CreditCard, Users,
  Download, MapPin, Lock, Activity, ChevronRight, ChevronDown,
  PartyPopper
} from '../components/animated-icons';
import {
  Building2, XCircle, BarChart3, Shield, Monitor,
  BadgeCheck, Award, Star, Menu, X
} from 'lucide-react';
import './LandingPage.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import api from '../utils/api';

// ─── Pricing Data ────────────────────────────────────────────────
const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    icon: <Rocket size={24} className="anim-pulse" />,
    monthlyPrice: 29,
    yearlyPrice: 24,
    desc: 'Perfect for freelancers & solo businesses',
    features: [
      { text: 'Up to 50 Invoices/month', included: true },
      { text: '5 Customers', included: true },
      { text: 'PDF Export', included: true },
      { text: 'Email Support', included: true },
      { text: 'Custom Branding', included: false },
      { text: 'Analytics & Reports', included: false },
      { text: 'Multi-user Access', included: false },
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    icon: <Zap size={24} className="anim-pulse" />,
    monthlyPrice: 69,
    yearlyPrice: 57,
    desc: 'For growing businesses & teams',
    popular: true,
    features: [
      { text: 'Unlimited Invoices', included: true },
      { text: 'Unlimited Customers', included: true },
      { text: 'PDF & Excel Export', included: true },
      { text: 'Priority Support', included: true },
      { text: 'Custom Branding & Logo', included: true },
      { text: 'Analytics & Reports', included: true },
      { text: 'Multi-user Access (3 seats)', included: false },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    icon: <Building2 size={24} className="anim-pulse" />,
    monthlyPrice: 149,
    yearlyPrice: 124,
    desc: 'For large teams & enterprises',
    features: [
      { text: 'Unlimited Invoices', included: true },
      { text: 'Unlimited Customers', included: true },
      { text: 'PDF, Excel & CSV Export', included: true },
      { text: 'Dedicated Support', included: true },
      { text: 'White-label Branding', included: true },
      { text: 'Advanced Analytics', included: true },
      { text: 'Unlimited Team Members', included: true },
    ],
  },
];

// ─── Testimonials ─────────────────────────────────────────────────
const TESTIMONIALS = [
  {
    quote: 'FastInvoice.in completely transformed how I handle billing. I used to spend hours creating invoices in Excel — now it takes 2 minutes!',
    name: 'Rajesh Sharma',
    role: 'Freelance Designer, Mumbai',
    avatar: 'RS',
    avatarBg: 'linear-gradient(135deg, #066478, #0793A7)',
    stars: 5,
  },
  {
    quote: 'The GST-ready invoices and instant PDF download are a lifesaver. My clients receive professional invoices within seconds of project completion.',
    name: 'Priya Nair',
    role: 'Boutique Owner, Bangalore',
    avatar: 'PN',
    avatarBg: 'linear-gradient(135deg, #EC4899, #F472B6)',
    stars: 5,
  },
  {
    quote: 'Best invoicing tool for Indian businesses. Generating professional invoices is incredibly fast and tracking unpaid invoices is a breeze.',
    name: 'Amit Gupta',
    role: 'IT Consultant, Delhi',
    avatar: 'AG',
    avatarBg: 'linear-gradient(135deg, #10B981, #34D399)',
    stars: 5,
  },
  {
    quote: 'We switched from Zoho to FastInvoice.in and saved 60% on our subscription costs. The features are on par and the UI is much cleaner.',
    name: 'Sunita Verma',
    role: 'CA & Tax Advisor, Pune',
    avatar: 'SV',
    avatarBg: 'linear-gradient(135deg, #F59E0B, #FCD34D)',
    stars: 5,
  },
  {
    quote: 'Creating GST-ready invoices takes less than a minute now. The interface is incredibly smooth and our clients love how professional the documents look.',
    name: 'Kiran Patel',
    role: 'Export Business, Surat',
    avatar: 'KP',
    avatarBg: 'linear-gradient(135deg, #8B5CF6, #A78BFA)',
    stars: 4,
  },
  {
    quote: 'FastInvoice.in helped me track overdue payments easily. The dashboard gives a clear picture of my business finances at a glance.',
    name: 'Meera Krishnan',
    role: 'Event Planner, Chennai',
    avatar: 'MK',
    avatarBg: 'linear-gradient(135deg, #06B6D4, #67E8F9)',
    stars: 5,
  },
];

// ─── FAQ Data ─────────────────────────────────────────────────────
const FAQS = [
  {
    q: 'Is FastInvoice.in compliant with GST regulations?',
    a: 'Yes! All invoices generated on FastInvoice.in are fully GST-compliant with proper GSTIN fields, HSN/SAC codes, and tax breakdowns (CGST, SGST, IGST). Suitable for all types of Indian businesses.',
  },
  {
    q: 'How does the subscription work?',
    a: 'After selecting a plan, you create your account credentials (email & password). Your account and data are isolated — only accessible with your login. Payments are processed securely via Razorpay.',
  },
  {
    q: 'Can I cancel my subscription anytime?',
    a: 'Absolutely. You can cancel your subscription at any time from your account settings. Your data remains accessible until the end of your billing period.',
  },
  {
    q: 'Is my invoice data secure?',
    a: 'Yes. All data is encrypted at rest and in transit using industry-standard SSL encryption. Each account has completely isolated database access — your data is never shared.',
  },
  {
    q: 'Can I try FastInvoice.in for free?',
    a: 'We offer a 7-day free trial on the Professional plan — no credit card required. Simply sign up and explore all features before committing.',
  },
  {
    q: 'What payment methods do you support for the subscription?',
    a: 'For your FastInvoice subscription, we securely process payments via Razorpay which supports UPI, Credit/Debit Cards, Net Banking, Wallets, and EMI options.',
  },
];

const loadRazorpay = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// ─── MODAL COMPONENT ──────────────────────────────────────────────
function PaymentModal({ plan, billing, isTrialMode, onClose }) {
  const { user } = useAuth();
  const isUpgrade = !!user;
  const [step, setStep] = useState(isUpgrade ? 2 : 1);
  const [form, setForm] = useState({ 
    email: user?.email || '', 
    password: '', 
    confirmPassword: '', 
    name: user?.name || '', 
    phone: '' 
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const price = billing === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors(prev => ({ ...prev, [e.target.name]: '' }));
  };



  const validateStep1 = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Full name is required';
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) errs.email = 'Valid email required';
    if (!form.phone.match(/^\+?\d{10,12}$/)) errs.phone = 'Valid 10-digit mobile number required';
    if (form.password.length < 8) errs.password = 'Password must be at least 8 characters';
    if (form.password !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleStep1 = async (e) => {
    e.preventDefault();
    if (validateStep1()) {
      setLoading(true);
      try {
        const res = await api.post('/auth/check-email', { email: form.email });
        const data = res.data;
        
        if (data.exists) {
          setErrors(prev => ({ ...prev, email: 'Email is already registered. Please log in to upgrade.' }));
          setLoading(false);
          return;
        }
        
        if (isTrialMode) {
          handleTrialStart();
        } else {
          handlePayment();
        }
      } catch (err) {
        console.warn('check-email failed, proceeding anyway:', err);
        if (isTrialMode) {
          handleTrialStart();
        } else {
          handlePayment();
        }
      }
    }
  };

  const handleTrialStart = async () => {
    try {
      const res = await api.post('/auth/start-trial', {
        name: form.name,
        email: form.email,
        password: form.password
      });
      
      if (res.data.success) {
        localStorage.setItem('token', res.data.token);
        setStep(3);
      } else {
        alert(res.data.message || 'Failed to start trial');
        setLoading(false);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to start trial.');
      setLoading(false);
    }
  };

  const handlePayment = async (e) => {
    e && e.preventDefault();
    setLoading(true);

    const res = await loadRazorpay();
    if (!res) {
      alert('Razorpay SDK failed to load. Are you online?');
      setLoading(false);
      return;
    }

    try {
      // 1. Create Order
      const orderResponse = await api.post('/payment/create-order', {
        amount: price,
        planType: plan.id
      });

      const orderData = orderResponse.data;
      if (!orderData.success) {
        alert('Server error. Could not create order.');
        setLoading(false);
        return;
      }

      // 2. Initialize Razorpay Checkout
      const options = {
        key: 'rzp_test_T6h4R2DXHkW5Ow', 
        amount: orderData.order.amount,
        currency: orderData.order.currency,
        name: 'FastInvoice.in',
        description: `Subscription for ${plan.name} Plan`,
        order_id: orderData.order.id,
        handler: async function (response) {
          try {
            // 3. Verify Payment and Create User
            const verifyRes = await api.post('/payment/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              user: {
                name: form.name,
                email: form.email,
                password: form.password,
                planType: plan.id,
                billingCycle: billing,
                isUpgrade: isUpgrade
              }
            });

            const verifyData = verifyRes.data;
            if (verifyData.success) {
              setStep(3);
              // Store token for auto login if you want
              localStorage.setItem('token', verifyData.token);
            } else {
              alert(verifyData.message || 'Payment verification failed!');
            }
          } catch (err) {
            alert('Error during verification.');
          }
        },
        prefill: {
          name: form.name,
          email: form.email,
          contact: form.phone
        },
        theme: {
          color: '#10B981' // primary color
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.on('payment.failed', function (response) {
        alert('Payment Failed! ' + response.error.description);
      });
      paymentObject.open();

    } catch (err) {
      alert(err.message === 'Network Error' ? 'Network Error: Unable to connect to the server. (Check CORS or API URL)' : 'Something went wrong: ' + (err.response?.data?.message || err.message));
    }
    setLoading(false);
  };

  return (
    <div className="payment-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="payment-modal">
        {/* Header */}
        <div className="modal-header">
          <div className="modal-plan-info">
            <div className="modal-plan-icon">{plan.icon}</div>
            <div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 700 }}>{plan.name} Plan</h3>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>₹{price}/month · {billing === 'yearly' ? 'Billed Annually' : 'Billed Monthly'}</p>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}><XCircle size={24} /></button>
        </div>

        <div className="modal-body">
          {/* Step 1: Account Creation */}
          {step === 1 && (
            <form onSubmit={handleStep1}>
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '4px' }}>Create Your Account</h4>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>Your dedicated workspace will be ready instantly after signup.</p>
              </div>

              <div className="form-group">
                <label>Full Name</label>
                <input name="name" value={form.name} onChange={handleChange} placeholder="Rajesh Sharma" />
                {errors.name && <span style={{ color: 'var(--secondary)', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{errors.name}</span>}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email Address</label>
                  <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@company.com" />
                  {errors.email && <span style={{ color: 'var(--secondary)', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{errors.email}</span>}
                </div>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input name="phone" value={form.phone} onChange={handleChange} placeholder="9876543210" />
                  {errors.phone && <span style={{ color: 'var(--secondary)', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{errors.phone}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Password</label>
                  <input name="password" type="password" value={form.password} onChange={handleChange} placeholder="Min. 8 characters" />
                  {errors.password && <span style={{ color: 'var(--secondary)', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{errors.password}</span>}
                </div>
                <div className="form-group">
                  <label>Confirm Password</label>
                  <input name="confirmPassword" type="password" value={form.confirmPassword} onChange={handleChange} placeholder="Repeat password" />
                  {errors.confirmPassword && <span style={{ color: 'var(--secondary)', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{errors.confirmPassword}</span>}
                </div>
              </div>

              <button type="submit" className="btn-modal-submit" disabled={loading}>
                {loading ? (
                  <>
                    <Activity size={18} className="anim-spin-slow" /> Processing...
                  </>
                ) : isTrialMode ? (
                  <>Start 7-Day Free Trial <ChevronRight size={18} /></>
                ) : (
                  <>Pay ₹{price} via Razorpay <ChevronRight size={18} /></>
                )}
              </button>
            </form>
          )}

          {/* Step 2: Confirmation for Logged-In Users */}
          {step === 2 && isUpgrade && (
            <div className="upgrade-confirmation">
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '4px' }}>Upgrade to {plan.name}</h4>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                  Please confirm your email address to proceed with the upgrade.
                </p>
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label>Confirm Email Address</label>
                <input 
                  type="email" 
                  name="confirmUpgradeEmail"
                  value={form.confirmUpgradeEmail || ''} 
                  onChange={handleChange} 
                  placeholder="Enter your logged-in email" 
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)' }}
                />
                {errors.confirmUpgradeEmail && <span style={{ color: 'var(--secondary)', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{errors.confirmUpgradeEmail}</span>}
              </div>

              <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span>Plan</span>
                  <strong>{plan.name} Plan</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span>Billing Cycle</span>
                  <strong>{billing === 'yearly' ? 'Annually' : 'Monthly'}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid var(--border-color)', marginTop: '8px' }}>
                  <span>Total Amount</span>
                  <strong style={{ fontSize: '1.2rem', color: 'var(--primary)' }}>₹{price}</strong>
                </div>
              </div>

              <button 
                className="btn-modal-submit" 
                onClick={() => {
                  if (form.confirmUpgradeEmail !== user.email) {
                    setErrors({ confirmUpgradeEmail: 'Email does not match your account email.' });
                    return;
                  }
                  handlePayment();
                }} 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Activity size={18} className="anim-spin-slow" /> Processing...
                  </>
                ) : (
                  <>Pay ₹{price} Now <ChevronRight size={18} /></>
                )}
              </button>
            </div>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <div className="success-state">
              <div className="success-icon"><PartyPopper size={40} className="anim-pulse" /></div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '8px' }}>You're all set!</h3>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>Account created successfully for</p>
              <p style={{ color: 'var(--primary-light)', fontWeight: 600, fontSize: '1.1rem', marginBottom: '16px' }}>{form.email}</p>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '32px', lineHeight: 1.6 }}>
                A confirmation email has been sent. Your {plan.name} plan is now active. Login to access your dashboard.
              </p>
              <button
                className="btn-modal-submit"
                style={{ maxWidth: '240px', margin: '0 auto' }}
                onClick={() => window.location.href = '/login'}
              >
                Go to Dashboard <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



// ─── MAIN LANDING PAGE ────────────────────────────────────────────
export default function LandingPage() {
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [billing, setBilling] = useState('monthly');
  const [openFaq, setOpenFaq] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isTrialMode, setIsTrialMode] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 800, once: true, offset: 100 });
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);

    if (window.location.hash) {
      setTimeout(() => {
        const id = window.location.hash.replace('#', '');
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-page">
      <SEO />
      {/* ─── NAVBAR ─────────────────────────── */}
      <nav className={`lp-nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="lp-logo">
          <img src={require('../assets/logo-full.png')} alt="FastInvoice" style={{ height: '110px', width: 'auto' }} />
        </div>

        <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>

        <ul className={`lp-nav-links ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          <li><a href="#features" onClick={e => { e.preventDefault(); scrollTo('features'); setIsMobileMenuOpen(false); }}>Features</a></li>
          <li><a href="#how-it-works" onClick={e => { e.preventDefault(); scrollTo('how-it-works'); setIsMobileMenuOpen(false); }}>How it Works</a></li>
          <li><a href="#pricing" onClick={e => { e.preventDefault(); scrollTo('pricing'); setIsMobileMenuOpen(false); }}>Pricing</a></li>
          <li><a href="#testimonials" onClick={e => { e.preventDefault(); scrollTo('testimonials'); setIsMobileMenuOpen(false); }}>Reviews</a></li>
          <li><a href="#faq" onClick={e => { e.preventDefault(); scrollTo('faq'); setIsMobileMenuOpen(false); }}>FAQ</a></li>
        </ul>
        <div className={`lp-nav-cta ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          <button className="btn-nav-login" onClick={() => window.location.href = '/login'}>Log In</button>
          <button className="btn-nav-primary" onClick={() => scrollTo('pricing')}>Start Free Trial <ChevronRight size={16} /></button>
        </div>
      </nav>

      {/* ─── HERO ───────────────────────────── */}
      <section className="lp-hero">
        <div className="hero-bg-glow"></div>
        <div className="hero-bg-glow-2"></div>

        <div className="hero-content" data-aos="fade-right">
          <div className="hero-badge">
            <Zap size={16} className="hero-badge-icon anim-pulse" />
            Made for Indian Businesses · GST Ready
          </div>

          <h1 className="hero-title">
            Invoice Faster.<br />
            Get Paid <span className="gradient-text">Sooner.</span><br />
            Grow Bigger.
          </h1>

          <p className="hero-subtitle">
            FastInvoice.in is the simplest, most powerful invoicing & billing platform built for Indian freelancers, SMEs, and enterprises. Create GST-ready invoices in 60 seconds.
          </p>

          <div className="hero-actions">
            <button className="btn-hero-primary" onClick={() => { setIsTrialMode(true); setSelectedPlan(PLANS.find(p => p.id === 'starter')); }}>
              Start Free Trial
              <ChevronRight size={20} />
            </button>
            <button className="btn-hero-secondary" onClick={() => scrollTo('how-it-works')}>
              See How It Works
            </button>
          </div>

          <div className="hero-stats">
            <div className="hero-stat">
              <span className="stat-number">10K+</span>
              <span className="stat-label">Active Businesses</span>
            </div>
            <div className="hero-stat">
              <span className="stat-number">₹50Cr+</span>
              <span className="stat-label">Invoices Generated</span>
            </div>
            <div className="hero-stat">
              <span className="stat-number">99.9%</span>
              <span className="stat-label">Uptime Guaranteed</span>
            </div>
          </div>
        </div>

        {/* Hero Visual Column */}
        <div className="hero-visual" data-aos="fade-left">
          <img src="/hero-illustration.jpg" alt="FastInvoice App" className="hero-illustration" />
        </div>
      </section>

      {/* ─── TRUST BADGES ───────────────────── */}
      <div className="lp-trust" data-aos="fade-up">
        <div className="trust-inner">
          <p className="trust-label">Trusted by 10,000+ Businesses Across India</p>
          <div className="trust-badges">
            <div className="trust-badge"><Lock size={20} /> SSL Secured</div>
            <div className="trust-badge"><BadgeCheck size={20} /> GST Compliant</div>
            <div className="trust-badge"><Zap size={20} /> Razorpay Powered</div>
            <div className="trust-badge"><Activity size={20} /> 99.9% Uptime</div>
            <div className="trust-badge"><Award size={20} /> ISO Certified</div>
          </div>
        </div>
      </div>

      {/* ─── FEATURES ───────────────────────── */}
      <section id="features" className="lp-section">
        <div className="text-center">
          <div className="section-label"><Zap size={16} /> Features</div>
          <h2 className="section-title" data-aos="fade-up">Everything You Need to Run Your Business</h2>
          <p className="section-sub" data-aos="fade-up" data-aos-delay="100">From creating invoices to tracking payments — FastInvoice.in handles it all so you can focus on growing your business.</p>
        </div>

        <div className="lp-features-grid">
          {[
            { Icon: Zap, color: '#818CF8', title: 'Instant Invoice Creation', desc: 'Create professional GST-ready invoices in under 60 seconds. Add items, taxes, discounts, and custom notes effortlessly.' },
            { Icon: FileText, color: '#34D399', title: 'Quotation Management', desc: 'Convert quotations to invoices with one click. Track acceptance rates and follow up with clients automatically.' },
            { Icon: CreditCard, color: '#F472B6', title: 'Online Payment Collection', desc: 'Collect payments instantly via Razorpay — UPI, cards, net banking, wallets, and EMI all supported in one link.' },
            { Icon: BarChart3, color: '#FBBF24', title: 'Analytics & Reports', desc: 'Get deep insights into your revenue, outstanding payments, top clients, and business trends with beautiful charts.' },
            { Icon: Users, color: '#60A5FA', title: 'Customer Management', desc: 'Maintain a complete customer database with contact details, transaction history, and outstanding balances.' },
            { Icon: Download, color: '#2DD4BF', title: 'PDF & Export', desc: 'Download pixel-perfect PDF invoices with your logo and branding. Export data to Excel for your accountant.' },
            { Icon: Shield, color: '#A78BFA', title: 'Secure & Private', desc: 'Each business gets an isolated, encrypted database. Your data is never shared. Bank-grade security always on.' },
            { Icon: Monitor, color: '#FB923C', title: 'Works on Any Device', desc: 'Access your invoices from desktop, tablet, or mobile. Fully responsive design that works everywhere.' },
            { Icon: MapPin, color: '#F87171', title: 'Built for India', desc: 'CGST, SGST, IGST, HSN codes, PAN, TAN — every Indian business requirement is built right in.' },
          ].map((f, index) => {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const iconRef = React.useRef(null);
            return (
              <div
                key={f.title}
                className="feature-card"
                data-aos="fade-up"
                data-aos-delay={index * 100}
                onMouseEnter={() => iconRef.current?.startAnimation?.()}
                onMouseLeave={() => iconRef.current?.stopAnimation?.()}
              >
                <div className="feature-icon-wrapper" style={{ color: f.color, border: `1px solid ${f.color}30` }}>
                  <f.Icon ref={iconRef} size={24} />
                </div>
                <h3 className="feature-title">{f.title}</h3>
                <p className="feature-desc">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ─── HOW IT WORKS ───────────────────── */}
      <section id="how-it-works" className="lp-section">
        <div className="how-container">
          <div>
            <div className="section-label"><Activity size={16} /> How It Works</div>
            <h2 className="section-title" data-aos="fade-up">From Signup to First Invoice in Minutes</h2>
            <p className="section-sub" data-aos="fade-up" data-aos-delay="100">Getting started with FastInvoice.in is ridiculously simple. No complex setup, no training required.</p>

            <div className="how-steps">
              {[
                { icon: <MapPin size={24} />, title: 'Choose Your Plan', desc: 'Pick from Starter, Professional, or Enterprise. Start with a 7-day free trial — no credit card needed.' },
                { icon: <Lock size={24} />, title: 'Create Your Account', desc: 'Enter your business details, email & password. Your private, encrypted workspace is ready instantly.' },
                { icon: <Building2 size={24} className="anim-pulse" />, title: 'Add Your Business Details', desc: 'Set up your company name, logo, GST number, bank details, and invoice preferences in 2 minutes.' },
                { icon: <FileText size={24} />, title: 'Create & Send Invoices', desc: 'Start creating professional invoices, add clients, products/services, and send via email with one click.' },
                { icon: <CreditCard size={24} />, title: 'Track & Manage Invoices', desc: 'Keep track of paid and unpaid invoices with a clean dashboard. Never lose sight of your business finances.' },
              ].map((step, i) => (
                <div key={i} className="how-step" data-aos="fade-right" data-aos-delay={i * 100}>
                  <div className="step-icon">{step.icon}</div>
                  <div className="step-content">
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div data-aos="fade-left" style={{ width: '100%' }}>
            {/* Dedicated Mockup Layout */}
            <div className="how-visual-mockup">
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #f3f4f6', paddingBottom: '20px', marginBottom: '24px' }}>
                <div>
                  <div style={{ color: 'var(--primary)', fontFamily: 'Playfair Display', fontWeight: 800, fontSize: '1.4rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img src={require('../assets/logo-full.png')} alt="FastInvoice" style={{ height: '44px', width: 'auto' }} />
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>Your Business Name</div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>GST: 27AABCU9603R1ZV</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ background: 'var(--primary)', color: 'white', padding: '6px 16px', borderRadius: '6px', fontWeight: 700, fontSize: '0.85rem' }}>TAX INVOICE</div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '8px' }}>#INV-2025-0042</div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Date: 26 Jun 2025</div>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600, letterSpacing: '0.05em', marginBottom: '4px' }}>BILL TO</div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#111' }}>Acme Corporation Pvt. Ltd.</div>
                <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>GSTIN: 29AABCT1332L1ZB</div>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', color: '#6b7280' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>#</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Description</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Qty</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Rate</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px' }}>1</td><td style={{ padding: '12px', fontWeight: 500 }}>Website Design</td><td style={{ padding: '12px', textAlign: 'right' }}>1</td><td style={{ padding: '12px', textAlign: 'right' }}>₹30,000</td><td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>₹30,000</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px' }}>2</td><td style={{ padding: '12px', fontWeight: 500 }}>SEO Services</td><td style={{ padding: '12px', textAlign: 'right' }}>3</td><td style={{ padding: '12px', textAlign: 'right' }}>₹4,000</td><td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>₹12,000</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px' }}>3</td><td style={{ padding: '12px', fontWeight: 500 }}>Hosting (Annual)</td><td style={{ padding: '12px', textAlign: 'right' }}>1</td><td style={{ padding: '12px', textAlign: 'right' }}>₹2,500</td><td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>₹2,500</td>
                  </tr>
                </tbody>
              </table>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '100px 100px', textAlign: 'right', color: '#4b5563' }}><span>Subtotal</span><span style={{ fontWeight: 600 }}>₹44,500</span></div>
                <div style={{ display: 'grid', gridTemplateColumns: '100px 100px', textAlign: 'right', color: '#4b5563' }}><span>CGST 9%</span><span style={{ fontWeight: 600 }}>₹4,005</span></div>
                <div style={{ display: 'grid', gridTemplateColumns: '100px 100px', textAlign: 'right', color: '#4b5563' }}><span>SGST 9%</span><span style={{ fontWeight: 600 }}>₹4,005</span></div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '24px', padding: '20px', background: '#f9fafb', borderRadius: '8px' }}>
                <div style={{ background: '#d1fae5', color: '#059669', padding: '6px 16px', borderRadius: '100px', fontWeight: 700, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}><CheckCircle2 size={16} /> PAID</div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: 600 }}>Total Due</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--primary)', lineHeight: 1 }}>₹52,510</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PRICING ────────────────────────── */}
      <section id="pricing" className="lp-section lp-pricing">
        <div className="text-center">
          <div className="section-label" style={{ background: '#ecfdf5', color: '#10b981' }}><Rocket size={16} /> 7-Day Free Trial</div>
          <h2 className="section-title" data-aos="fade-up">Simple, Transparent Pricing</h2>
          <p className="section-sub" data-aos="fade-up" data-aos-delay="100">Try our Starter plan completely free for 7 days. No credit card required. Upgrade, downgrade, or cancel anytime.</p>

          <div className="pricing-toggle">
            <button className={`toggle-btn ${billing === 'monthly' ? 'active' : ''}`} onClick={() => setBilling('monthly')}>Monthly</button>
            <button className={`toggle-btn ${billing === 'yearly' ? 'active' : ''}`} onClick={() => setBilling('yearly')}>Yearly</button>
            {billing === 'yearly' && <span className="save-badge">Save 17%</span>}
          </div>
        </div>

        <div className="pricing-grid">
          {PLANS.map((plan, i) => (
            <div key={plan.id} className={`pricing-card ${plan.popular ? 'popular' : ''}`} data-aos="fade-up" data-aos-delay={i * 150}>
              {plan.popular && <div className="popular-badge"><Star size={16} fill="currentColor" /> Most Popular</div>}
              <div className="pricing-icon">{plan.icon}</div>
              <h3 className="pricing-name">{plan.name} Plan</h3>
              <div className="pricing-price-wrapper">
                <span className="price-currency">₹</span>
                <span className="price-amount">{billing === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice}</span>
                <span className="price-period">/mo</span>
              </div>
              <p className="price-desc">{plan.desc}</p>

              <ul className="pricing-features">
                {plan.features.map((f, idx) => (
                  <li key={idx}>
                    {f.included ? <CheckCircle2 size={20} className="feature-check" /> : <XCircle size={20} className="feature-cross" />}
                    <span className={`feature-text ${f.included ? '' : 'disabled'}`}>{f.text}</span>
                  </li>
                ))}
              </ul>

              <button
                className={`btn-pricing ${plan.id === 'starter' && !user?.hasUsedTrial ? 'trial-btn' : plan.popular ? 'filled' : 'outline'}`}
                style={plan.id === 'starter' && !user?.hasUsedTrial ? { background: '#10B981', color: '#fff', border: 'none' } : {}}
                onClick={() => {
                  if (plan.id === 'starter' && !user?.hasUsedTrial) {
                    setIsTrialMode(true);
                  }
                  setSelectedPlan(plan);
                }}
              >
                {plan.id === 'starter' && !user?.hasUsedTrial 
                  ? <><Rocket size={18} /> Start 7-Day Free Trial</> 
                  : plan.popular 
                    ? <><Zap size={18} /> Get Started</> 
                    : 'Choose Plan'}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ─── TESTIMONIALS ───────────────────── */}
      <section id="testimonials" className="lp-section">
        <div className="text-center">
          <div className="section-label"><Star size={16} /> Customer Reviews</div>
          <h2 className="section-title" data-aos="fade-up">Loved by Indian Businesses</h2>
          <p className="section-sub" data-aos="fade-up" data-aos-delay="100">Don't just take our word for it. See what our active users have to say about FastInvoice.in.</p>
        </div>

        <div className="testimonials-grid">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="testimonial-card" data-aos="zoom-in" data-aos-delay={i * 100}>
              <div className="tc-stars">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} size={18} fill={j < t.stars ? "currentColor" : "none"} strokeWidth={j < t.stars ? 0 : 2} />
                ))}
              </div>
              <p className="tc-quote">"{t.quote}"</p>
              <div className="tc-author">
                <div className="tc-avatar" style={{ background: t.avatarBg }}>{t.avatar}</div>
                <div>
                  <div className="tc-name">{t.name}</div>
                  <div className="tc-role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── FAQ ────────────────────────────── */}
      <section id="faq" className="lp-section">
        <div className="faq-container">
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <div className="section-label"><FileText size={16} /> FAQ</div>
            <h2 className="section-title">Frequently Asked Questions</h2>
          </div>

          {FAQS.map((faq, i) => (
            <div key={i} className={`faq-item ${openFaq === i ? 'open' : ''}`}>
              <div className="faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                {faq.q}
                <ChevronDown size={20} className="faq-toggle" />
              </div>
              <div className="faq-answer">{faq.a}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── CTA SECTION ────────────────────── */}
      <section className="lp-cta" data-aos="zoom-in">
        <div className="cta-container">
          <div className="cta-pattern"></div>
          <div className="cta-content">
            <h2 className="cta-title">Ready to Transform Your Billing Process?</h2>
            <p className="cta-sub">Join 10,000+ Indian businesses who trust FastInvoice.in to manage their invoicing. Start your 7-day free trial today.</p>
            <div className="cta-actions">
              <button className="btn-cta-white" onClick={() => { setIsTrialMode(true); setSelectedPlan(PLANS.find(p => p.id === 'starter')); }}>Start Free Trial <ChevronRight size={20} /></button>
              <button className="btn-cta-outline" onClick={() => window.location.href = '/login'}>Login to Dashboard</button>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* ─── PAYMENT MODAL ──────────────────── */}
      {selectedPlan && (
        <PaymentModal
          plan={selectedPlan}
          billing={billing}
          isTrialMode={isTrialMode}
          onClose={() => { setSelectedPlan(null); setIsTrialMode(false); }}
        />
      )}
    </div>
  );
}
